import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/utils';

function Dropdown({ children, className }) {
  return <div className={cn('tw-inline-flex tw-items-center', className)}>{children}</div>;
}

const DropdownTrigger = React.forwardRef(({ children, className, ...props }, ref) => {
  const dataState = props['data-state'];
  return (
    <button
      type="button"
      ref={ref}
      className={cn(
        'tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white/95 tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-600 tw-backdrop-blur-sm tw-transition hover:tw-border-indigo-300 hover:tw-text-indigo-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-300 dark:tw-border-slate-700 dark:tw-bg-slate-900/80 dark:tw-text-slate-200 dark:hover:tw-border-indigo-400 dark:hover:tw-text-indigo-300',
        className,
      )}
      {...props}
      data-state={dataState}
    >
      {children}
      <span className="material-symbols-outlined tw-ml-1 tw-text-base tw-transition-transform data-[state=open]:tw-rotate-180" data-state={dataState}>
        expand_more
      </span>
    </button>
  );
});
DropdownTrigger.displayName = 'DropdownTrigger';

function DropdownContent({ open, onClose, children, align = 'start', anchorRef }) {
  const contentRef = React.useRef(null);
  const [position, setPosition] = React.useState({ top: 0, left: 0, transform: '' });

  const updatePosition = React.useCallback(() => {
    const anchor = anchorRef?.current;
    if (!anchor) {
      return;
    }

    const rect = anchor.getBoundingClientRect();
    const offset = 8;
    const top = rect.bottom + window.scrollY + offset;
    let left = rect.left + window.scrollX;
    let transform = '';

    if (align === 'center') {
      left = rect.left + window.scrollX + rect.width / 2;
      transform = 'translateX(-50%)';
    } else if (align === 'end') {
      left = rect.right + window.scrollX;
      transform = 'translateX(-100%)';
    }

    setPosition({ top, left, transform });
  }, [anchorRef, align]);

  React.useLayoutEffect(() => {
    if (open) {
      updatePosition();
    }
  }, [open, updatePosition]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const handleResizeOrScroll = () => {
      updatePosition();
    };

    window.addEventListener('resize', handleResizeOrScroll);
    window.addEventListener('scroll', handleResizeOrScroll, true);

    return () => {
      window.removeEventListener('resize', handleResizeOrScroll);
      window.removeEventListener('scroll', handleResizeOrScroll, true);
    };
  }, [open, updatePosition]);

  React.useEffect(() => {
    function handleClickOutside(event) {
      const anchor = anchorRef?.current;
      if (
        contentRef.current &&
        !contentRef.current.contains(event.target) &&
        (!anchor || !anchor.contains(event.target))
      ) {
        onClose?.();
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        onClose?.();
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose, anchorRef]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      ref={contentRef}
      className="tw-z-[9999] tw-min-w-[220px]"
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        transform: position.transform,
      }}
    >
      <div className="tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/95 tw-p-3 tw-shadow-xl tw-backdrop-blur dark:tw-border-slate-700 dark:tw-bg-slate-900/90 dark:tw-shadow-none">
        {children}
      </div>
    </div>,
    document.body,
  );
}

function DropdownCheckboxItem({ label, checked, disabled, onCheckedChange }) {
  return (
    <label
      className={cn(
        'tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-xl tw-px-2 tw-py-1.5 tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-slate-600 hover:tw-bg-slate-100 dark:tw-text-slate-300 dark:hover:tw-bg-slate-800/60',
        disabled && 'tw-cursor-not-allowed tw-opacity-60',
      )}
    >
      <input
        type="checkbox"
        className="tw-form-checkbox tw-h-4 tw-w-4 tw-rounded tw-border-slate-300 tw-text-indigo-500 focus:tw-ring-indigo-400 dark:tw-border-slate-600 dark:tw-bg-slate-800"
        checked={checked}
        onChange={(evento) => onCheckedChange?.(evento.target.checked)}
        disabled={disabled}
      />
      <span>{label}</span>
    </label>
  );
}

export { Dropdown, DropdownTrigger, DropdownContent, DropdownCheckboxItem };
